/**
 * ConnectDiscord.js
 *
 * Links a Discord account to a BotSpot user. Reached from a connect link
 * sent by the BotSpot Discord bot (token=<one-time-token>).
 *
 * Flow:
 *  1. Load: if not logged in -> Auth0 login with returnTo preserving the token.
 *  2. Authenticated: GET /connect/discord/preview to read the token WITHOUT
 *     consuming it. Shows the user which Discord handle is asking to be
 *     linked so they can explicitly Approve or Cancel.
 *  3. Approve -> POST /connect/discord/callback, which consumes the token
 *     and creates the ChannelIdentity.
 *  4. Cancel -> POST /connect/discord/cancel, which consumes the token
 *     without linking anything.
 *
 * Having an explicit approval step prevents a stolen/shared connect link
 * from being silently redeemed against the currently-logged-in BotSpot user.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useUser from "../../hooks/useUser";
import "./ConnectDiscord.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function ConnectDiscord() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, getApiToken, loginWithRedirect } = useUser();

  // idle | loading-preview | awaiting-approval | linking | success | expired | cancelled | error
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null); // { channelUsername, channelUserIdHint, expiresAt }

  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  // Step 1: gate on auth + token
  useEffect(() => {
    if (isLoading) return;

    if (!token) {
      setStatus("error");
      setMessage(
        "This connect link is missing its token. Return to Discord and message the bot again to get a fresh link."
      );
      return;
    }

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: `${location.pathname}${location.search}` },
      });
      return;
    }

    // Step 2: preview the token (does NOT consume it)
    let cancelled = false;
    setStatus("loading-preview");

    (async () => {
      try {
        const apiToken = await getApiToken();
        const response = await fetch(
          `${API_URL}/connect/discord/preview?token=${encodeURIComponent(token)}`,
          {
            headers: { Authorization: `Bearer ${apiToken}` },
          }
        );
        if (cancelled) return;

        const data = await response.json().catch(() => ({}));

        if (response.status === 200) {
          setPreview(data);
          setStatus("awaiting-approval");
          return;
        }
        if (response.status === 400 || response.status === 409) {
          setStatus("expired");
          setMessage(
            data.error ||
              "This connect link has expired or was already used. Return to Discord and message the bot again to get a fresh link."
          );
          return;
        }
        setStatus("error");
        setMessage(data.error || `Could not load this connect link (status ${response.status}).`);
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err.message || "Network error while loading connect preview.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getApiToken, loginWithRedirect, token, location.pathname, location.search]);

  // Approve -> call the real callback. Runs only when the user clicks Approve.
  const handleApprove = async () => {
    setStatus("linking");
    try {
      const apiToken = await getApiToken();
      const response = await fetch(
        `${API_URL}/connect/discord/callback?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (response.status === 200) {
        setStatus("success");
        setMessage(
          data.alreadyLinked
            ? "This Discord account is already connected to BotSpot. You are all set!"
            : "Discord connected. Head back to Discord and DM the BotSpot Agent to start chatting."
        );
        return;
      }
      if (response.status === 400 || response.status === 409) {
        setStatus("expired");
        setMessage(
          data.error ||
            "This connect link has expired or was already used. Return to Discord and message the bot again to get a fresh link."
        );
        return;
      }
      setStatus("error");
      setMessage(data.error || `Connection failed (status ${response.status}).`);
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Network error while connecting Discord.");
    }
  };

  // Cancel -> consume the token server-side so it can't be replayed.
  const handleCancel = async () => {
    try {
      const apiToken = await getApiToken();
      await fetch(`${API_URL}/connect/discord/cancel?token=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${apiToken}` },
      });
    } catch {
      // Best-effort cancel; even on network error we still move to the cancelled view.
    }
    setStatus("cancelled");
    setMessage("You cancelled the connection. No link was created.");
  };

  const renderPreviewTarget = () => {
    if (!preview) return null;
    const handle = preview.channelUsername
      ? `@${preview.channelUsername}`
      : preview.channelUserIdHint
        ? `Discord user ending in ...${preview.channelUserIdHint}`
        : "this Discord account";
    return handle;
  };

  return (
    <div className="connect-discord-container">
      <div className="connect-discord-card">
        <h1 className="connect-discord-title">Connect your Discord account</h1>

        {(status === "idle" || status === "loading-preview") && (
          <>
            <p className="connect-discord-body">Loading connect request...</p>
            <div className="connect-discord-spinner" aria-hidden="true" />
          </>
        )}

        {status === "awaiting-approval" && preview && (
          <>
            <p className="connect-discord-body">
              <strong>{renderPreviewTarget()}</strong> wants to connect to your BotSpot account.
            </p>
            <p className="connect-discord-body connect-discord-body--muted">
              Approving lets this Discord account DM the BotSpot Agent as you and see your strategies.
              Only approve if you just requested this from Discord.
            </p>
            <div className="connect-discord-button-row">
              <button
                type="button"
                className="connect-discord-cta connect-discord-cta--primary"
                onClick={handleApprove}
              >
                Approve & Connect
              </button>
              <button
                type="button"
                className="connect-discord-cta connect-discord-cta--secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {status === "linking" && (
          <>
            <p className="connect-discord-body">Connecting...</p>
            <div className="connect-discord-spinner" aria-hidden="true" />
          </>
        )}

        {status === "success" && (
          <>
            <div className="connect-discord-checkmark" aria-hidden="true">
              ✓
            </div>
            <p className="connect-discord-body">{message}</p>
            <button
              type="button"
              className="connect-discord-cta"
              onClick={() => navigate("/ai-agent")}
            >
              Go to AI Agent
            </button>
          </>
        )}

        {status === "cancelled" && (
          <>
            <p className="connect-discord-body">{message}</p>
            <button
              type="button"
              className="connect-discord-cta"
              onClick={() => navigate("/")}
            >
              Back to BotSpot
            </button>
          </>
        )}

        {(status === "expired" || status === "error") && (
          <>
            <p className="connect-discord-body connect-discord-body--error">{message}</p>
            <button
              type="button"
              className="connect-discord-cta"
              onClick={() => navigate("/")}
            >
              Back to BotSpot
            </button>
          </>
        )}
      </div>
    </div>
  );
}
